import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Empty, notification, Tooltip } from "antd";
import Table from "../../../components/Table";
import { Bar, Pie } from "react-chartjs-2";
import { HiOutlineMagnifyingGlass, HiOutlinePencil } from "react-icons/hi2";

import AnimatedMainContainer from "../../../components/AnimatedMainContainer";
import EventSelectionModal from "../../AdminDocuments/components/EventSelectionModal";

import {
  exportDashboardLogs,
  getAllTotems,
  getDashboardLogs,
  getDashboardStats,
} from "../../../service/manage.access";

import {
  Container,
  Title,
  PieChartContainer,
  ChartCard,
  TitleContainer,
  ChartFiltersContainer,
  DashboardTableContainer,
  TitleAndFilterContainer,
  ExportButton,
} from "../styles";
import SelectWithInternalLabel from "../../../newUXComponents/SelectWithInternalLabel";
import { format } from "date-fns";
import { SearchTableInput } from "../../AdminDocuments/ApprovalDocuments/styles";
import { AiOutlineDownload } from "react-icons/ai";
import { getAllCredentialTypes } from "../../../service/credentials.service";
import {
  getReportFilterEventsByYearService,
  getReportFilterYearsService,
} from "../../../service/reportFilter.service";

const AdminManageTotemDashboard = () => {
  const intl = useIntl();
  const { token } = useSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(true);

  const [yearsOptions, setYearsOptions] = useState([]);
  const [eventsOptions, setEventsOptions] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [printedCount, setPrintedCount] = useState(0);
  const [notPrintedCount, setNotPrintedCount] = useState(0);

  const [topPrintedTotems, setTopPrintedTotems] = useState([]);
  const [usageByPositionStats, setUsageByPositionStats] = useState([]);

  const [credentialTypes, setCredentialTypes] = useState([]);
  const [credentialTypeFilter, setCredentialTypeFilter] = useState(null);

  const [areasOptions, setAreasOptions] = useState([]);
  const [areaFilter, setAreaFilter] = useState(null);

  const [topErrorPositions, setTopErrorPositions] = useState([]);

  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sort, setSort] = useState();
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /* ------------------------------------------------------------------
   * Handlers / Services
   * ------------------------------------------------------------------ */

  const fetchYears = useCallback(async () => {
    getReportFilterYearsService({ token })
      .then((response) => {
        setYearsOptions(
          (response.data?.output ?? []).map((year) => ({
            label: String(year),
            value: year,
          }))
        );
      })
      .catch((error) => {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + error.response.data.code,
          }),
        });
      });
  }, [token, intl]);

  const fetchEventsByYear = useCallback(
    async (year) => {
      setEventsOptions([]);

      if (year === null || year === undefined) {
        return;
      }

      getReportFilterEventsByYearService({ token, year })
        .then((response) => {
          const options = (response.data?.output ?? [])
            .map((event) => ({
              label: event.eventEditionName,
              value: event.idNumber,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          setEventsOptions(options);
        })
        .catch((error) => {
          notification.error({
            message: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage({
              id: "api.error." + error.response.data.code,
            }),
          });
        });
    },
    [token, intl]
  );

  const fetchTotemAreas = useCallback(async () => {
    if (!selectedEvent) return;

    try {
      const responseTotems = await getAllTotems({ token });

      const filteredTotems = responseTotems.data.totems.filter(
        (totem) => totem.eventId === selectedEvent.value
      );

      const areasOptions = filteredTotems.map((totem) => ({
        value: totem.id,
        label: totem.areaName,
      }));

      setAreasOptions(areasOptions || []);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + error.response.data.code,
        }),
      });
    }
  }, [token, selectedEvent]);

  useEffect(() => {
    fetchTotemAreas();
  }, [fetchTotemAreas]);

  useEffect(() => {
    const loadCredentialTypes = async () => {
      try {
        const responseTypes = await getAllCredentialTypes({ token });
        const rawCredentialTypes = responseTypes.data.credentialTypeList;

        const options = rawCredentialTypes.map((type) => ({
          value: type,
          label: intl.formatMessage({ id: type }),
        }));

        setCredentialTypes(options);
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
        });
      }
    };

    loadCredentialTypes();
  }, [token, intl]);

  const loadDashboardDataStats = useCallback(async () => {
    if (!selectedYear || !selectedEvent) return;
    setIsLoading(true);

    try {
      const response = await getDashboardStats({
        token,
        eventId: selectedEvent.value,
        year: selectedYear,
        credentialType:
          credentialTypeFilter === "all" ? null : credentialTypeFilter,
        totemId: areaFilter === "all" ? null : areaFilter,
      });

      const {
        overall,
        topPrintedPositions,
        usageByPosition,
        topErrorPositions,
      } = response.data.data;

      setPrintedCount(overall.printed);
      setNotPrintedCount(overall.notPrinted);
      setTopPrintedTotems(topPrintedPositions);
      setUsageByPositionStats(usageByPosition);
      setTopErrorPositions(topErrorPositions);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: "api.error." + error.response?.data?.code,
        }),
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear, selectedEvent, credentialTypeFilter, areaFilter, token, intl]);

  const fetchDashboardLogs = useCallback(
    async ({
      token,
      year,
      eventId,
      page = 0,
      size = 10,
      sort = "dateTime,DESC",
      search,
      isExport = false,
    }) => {
      if (!year || !eventId) return;

      if (!isExport) setIsLoading(true);

      try {
        const response = isExport
          ? await exportDashboardLogs({
              token,
              year,
              eventId,
              page,
              size,
              sort,
              search,
            })
          : await getDashboardLogs({
              token,
              year,
              eventId,
              page,
              size,
              sort,
              search,
            });

        if (isExport) {
          const downloadUrl = response.data.data.downloadUrl;
          if (downloadUrl) {
            const link = document.createElement("a");
            link.href = downloadUrl;

            const fileName = `dashboard_logs_${new Date().getTime()}.csv`;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
          } else {
            notification.error({
              message: intl.formatMessage({ id: "error" }),
              description: intl.formatMessage({
                id: "error",
              }),
            });
          }
        } else {
          setLogs(response.data.data.content || []);
          setTotalRows(response.data.data.totalElements || 0);
        }
      } catch (error) {
        notification.error({
          message: intl.formatMessage({ id: "error" }),
          description: intl.formatMessage({
            id: "api.error." + error.response.data.code,
          }),
        });
      } finally {
        if (!isExport) setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  // Carrega dados do dashboard quando evento muda
  useEffect(() => {
    loadDashboardDataStats();
  }, [loadDashboardDataStats]);

  useEffect(() => {
    if (selectedYear && selectedEvent && token) {
      fetchDashboardLogs({
        token,
        eventId: selectedEvent.value,
        year: selectedYear,
        page,
        size,
        sort,
        search,
      });
    }
  }, [
    selectedYear,
    selectedEvent,
    token,
    page,
    size,
    sort,
    search,
    fetchDashboardLogs,
  ]);

  const handleEventSelect = (event, year) => {
    setSelectedYear(year);
    setSelectedEvent(event);
    setIsEventModalOpen(false);
    setCredentialTypeFilter();
    setAreaFilter();
    setSearchTerm("");
  };

  // Funções auxiliares para tabela

  const handleSortChange = (column, direction) => {
    if (!column || !direction) return;
    const sortDirection = direction === "asc" ? "ASC" : "DESC";
    setSort(`${column.sortField},${sortDirection}`);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSearch(value);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage - 1);
  };
  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setPage(0);
  };

  /* ------------------------------------------------------------------
   * Gráfico 1 — Pizza: Impressões realizadas x não realizadas
   * ------------------------------------------------------------------ */

  const totalPrints = printedCount + notPrintedCount;

  const printedPercentage = totalPrints
    ? Math.round((printedCount / totalPrints) * 100)
    : 0;

  const notPrintedPercentage = totalPrints
    ? Math.round((notPrintedCount / totalPrints) * 100)
    : 0;

  const printStatusPieData = {
    labels: [
      `${printedPercentage}% - ${intl.formatMessage({
        id: "print_performed",
      })}`,
      `${notPrintedPercentage}% - ${intl.formatMessage({
        id: "print_not_performed",
      })}`,
    ],
    datasets: [
      {
        data: [printedCount, notPrintedCount],
        backgroundColor: ["#16A34A", "#B91C1C"],
      },
    ],
  };

  /* ------------------------------------------------------------------
   * Gráfico 2 — Pizza: Top totens com mais impressões
   * ------------------------------------------------------------------ */

  const top4PrintedTotems = topPrintedTotems.filter(
    (item) => item.rank >= 1 && item.rank <= 4
  );

  const topTotemsPieData = {
    labels: top4PrintedTotems.map(
      (item) => `${item.totemName} - ${item.printed}`
    ),
    datasets: [
      {
        data: top4PrintedTotems.map((item) => item.printed),
        backgroundColor: ["#3B82F6", "#EF4444", "#F59E0B", "#10B981"],
      },
    ],
  };

  /* ------------------------------------------------------------------
   * Gráfico 3 — Barras empilhadas: Uso por posição (sucesso x erro)
   * ------------------------------------------------------------------ */

  const sortedUsageByPosition = [...usageByPositionStats].sort(
    (a, b) => b.success + b.errors - (a.success + a.errors)
  );

  const usageByPositionBarData = {
    labels: sortedUsageByPosition.map((item) => item.totemName),
    datasets: [
      {
        label: intl.formatMessage({ id: "performed" }),
        data: sortedUsageByPosition.map((item) => item.success),
        backgroundColor: "#10B981",
        stack: "total",
        maxBarThickness: 50,
      },
      {
        label: intl.formatMessage({ id: "not_performed" }),
        data: sortedUsageByPosition.map((item) => item.errors),
        backgroundColor: "#EF4444",
        stack: "total",
        maxBarThickness: 50,
      },
    ],
  };

  /* ------------------------------------------------------------------
   * Gráfico 4 — Barras empilhadas: Posições com mais erros
   * ------------------------------------------------------------------ */

  const top4ErrorPositions = topErrorPositions.filter(
    (item) => item.rank >= 1 && item.rank <= 4
  );

  const topErrorPositionsBarData = {
    labels: top4ErrorPositions.map((item) => item.totemName),
    datasets: [
      {
        label: "",
        data: sortedUsageByPosition.map((item) => item.errors),
        backgroundColor: "#EF4444",
        stack: "total",
        maxBarThickness: 50,
      },
    ],
  };

  /* ------------------------------------------------------------------
   * Opções padrão para gráficos de barras
   * ------------------------------------------------------------------ */

  const createBarChartOptions = (title, displayingLegend) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: title,
        font: { size: 16, weight: "bold" },
      },
      legend: {
        position: "bottom",
        display: displayingLegend,
      },
    },
    scales: {
      x: {
        offset: true,
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
      },
    },
  });

  /* ------------------------------------------------------------------
   * Opções padrão para gráficos de pizza
   * ------------------------------------------------------------------ */

  const createPieChartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: title,
        font: { size: 16, weight: "bold" },
        padding: { bottom: 20 },
      },
      legend: {
        position: "bottom",
        labels: { padding: 16 },
      },
      tooltip: false,
    },
    animation: {
      duration: 800,
    },
  });

  /* ------------------------------------------------------------------
   * Colunas da tabela
   * ------------------------------------------------------------------ */
  const columns = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: "date_time" }),
        selector: (row) => row.dateTime,
        sortField: "dateTime",
        cell: (row) => {
          const dateTimeText = row.dateTime
            ? format(new Date(row.dateTime), "dd/MM/yyyy - HH:mm")
            : "";
          return (
            <Tooltip title={dateTimeText} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {dateTimeText}
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        width: "180px",
      },
      {
        name: intl.formatMessage({ id: "credential_type" }),
        selector: (row) => row.credentialType,
        sortField: "credentialType",
        cell: (row) => {
          const credentialType = row.credentialType;
          const text = credentialType
            ? intl.formatMessage({
                id: credentialType,
                defaultMessage: credentialType,
              })
            : "";

          return (
            <Tooltip title={text} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {text}
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        width: "180px",
      },
      {
        name: intl.formatMessage({ id: "collaborator" }),
        selector: (row) => row.collaborator,
        sortField: "collaborator",
        cell: (row) => {
          const collaboratorText = row.collaborator || "";
          return (
            <Tooltip title={collaboratorText} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {collaboratorText}
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        width: "220px",
      },
      {
        name: intl.formatMessage({ id: "document" }),
        selector: (row) => row.document,
        sortField: "document",
        cell: (row) => {
          const docText = row.document || "";
          return (
            <Tooltip title={docText} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {docText}
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        width: "150px",
      },
      {
        name: intl.formatMessage({ id: "area_" }),
        selector: (row) => row.area,
        sortField: "area",
        cell: (row) => {
          const areaText = row.area || "";
          return (
            <Tooltip title={areaText} placement="topLeft">
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {areaText}
              </div>
            </Tooltip>
          );
        },
        sortable: true,
        width: "160px",
      },
      {
        name: intl.formatMessage({ id: "venue" }),
        selector: (row) => row.totem,
        sortField: "totem",
        cell: (row) => row.totem || "",
        sortable: true,
        width: "100px",
      },
      {
        name: intl.formatMessage({ id: "position" }),
        selector: (row) => row.position,
        sortField: "position",
        cell: (row) => row.position || "",
        sortable: true,
        width: "100px",
        center: true,
      },
      {
        name: intl.formatMessage({ id: "printed" }),
        selector: (row) => row.printed,
        sortField: "printed",
        cell: (row) =>
          row.printed
            ? intl.formatMessage({ id: "yes" })
            : intl.formatMessage({ id: "no" }),
        sortable: true,
        center: true,
        width: "120px",
      },
      {
        name: intl.formatMessage({ id: "status" }),
        selector: (row) => row.status,
        sortField: "status",
        cell: (row) => intl.formatMessage({ id: row.status }),
        sortable: true,
        center: true,
        width: "140px",
      },

      {
        name: intl.formatMessage({ id: "action" }),
        selector: (row) => row.action,
        sortField: "action",
        cell: (row) => intl.formatMessage({ id: row.action }),
        sortable: true,
        width: "auto",
      },
    ],
    [intl]
  );

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */

  return (
    <AnimatedMainContainer breadcrumb={intl.formatMessage({ id: "dashboard" })}>
      <Container>
        <TitleContainer withSelectEvent>
          <Title>
            {intl.formatMessage({ id: "dashboard_monitoring_totem" })} -
          </Title>

          <Title sub>
            {selectedEvent
              ? selectedEvent.label
              : intl.formatMessage({ id: "choose_event" })}
            <HiOutlinePencil
              size={20}
              style={{ cursor: "pointer" }}
              onClick={() => setIsEventModalOpen(true)}
            />
          </Title>
        </TitleContainer>

        <ChartFiltersContainer>
          <SelectWithInternalLabel
            label={
              intl.formatMessage({
                id: "credential_type",
              }) + ":"
            }
            options={credentialTypes}
            value={credentialTypeFilter}
            onChange={setCredentialTypeFilter}
          />
          <SelectWithInternalLabel
            label={
              intl.formatMessage({
                id: "totem(area)",
              }) + ":"
            }
            options={areasOptions}
            value={areaFilter}
            onChange={setAreaFilter}
          />
        </ChartFiltersContainer>

        {selectedEvent ? (
          <>
            <PieChartContainer>
              <ChartCard>
                <Pie
                  data={printStatusPieData}
                  options={createPieChartOptions(
                    intl.formatMessage({ id: "printed_x_not_printed" })
                  )}
                />
              </ChartCard>

              <ChartCard>
                <Pie
                  data={topTotemsPieData}
                  options={createPieChartOptions(
                    intl.formatMessage({
                      id: "positions_with_most_print",
                    })
                  )}
                />
              </ChartCard>
            </PieChartContainer>

            <ChartCard bar>
              <Bar
                data={usageByPositionBarData}
                options={createBarChartOptions(
                  intl.formatMessage({
                    id: "usage_by_position_success_error",
                  }),
                  true
                )}
              />
            </ChartCard>
            <ChartCard bar>
              <Bar
                data={topErrorPositionsBarData}
                options={createBarChartOptions(
                  intl.formatMessage({ id: "positions_with_more_errors" }),
                  false
                )}
              />
            </ChartCard>
            <DashboardTableContainer>
              <TitleAndFilterContainer>
                <div style={{ display: "flex", gap: "20px" }}>
                  <Title>
                    {`${intl.formatMessage({
                      id: "history_of_actions",
                    })} (${totalRows})`}
                  </Title>
                  <SearchTableInput
                    dash
                    placeholder={intl.formatMessage({
                      id: "totem_dashboard_search_input_label",
                    })}
                    prefix={<HiOutlineMagnifyingGlass color="#1E293B" />}
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>

                <div />
                <ExportButton
                  onClick={() =>
                    fetchDashboardLogs({
                      token,
                      eventId: selectedEvent?.value,
                      year: selectedYear,
                      sort,
                      search,
                      isExport: true,
                    })
                  }
                >
                  <AiOutlineDownload size={22} />
                  {intl.formatMessage({ id: "export" })}
                </ExportButton>
              </TitleAndFilterContainer>

              <Table
                data={logs}
                columns={columns}
                progressPending={isLoading}
                paginationServer={true}
                paginationTotalRows={totalRows}
                paginationDefaultPage={page + 1}
                paginationPerPage={size}
                onChangeRowsPerPage={handlePageSizeChange}
                onChangePage={handlePageChange}
                onSorted={handleSortChange}
              />
            </DashboardTableContainer>
          </>
        ) : (
          <Empty />
        )}
      </Container>

      <EventSelectionModal
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
        onEventSelect={handleEventSelect}
        events={eventsOptions}
        years={yearsOptions}
        onYearSelect={fetchEventsByYear}
        loading={isLoading}
      />
    </AnimatedMainContainer>
  );
};

export default AdminManageTotemDashboard;
